import { Controller, useController, useFormContext, useWatch } from 'react-hook-form';
import { OnboardingFormData } from './FYIGroupsOnboarding';
import { Checkbox } from '@cimplico/components';
import MonthSelect from '../../../../../../../components/month-select/MonthSelect';
import DaySelect from '../../../../../../../components/day-select/DaySelect';
import BusinessStructureBadge from '../BusinessStructureBadge';

type FYIEntityRowProps = {
    groupIndex: number;
    index: number;
};

function FYIEntityRow({ groupIndex, index }: FYIEntityRowProps): JSX.Element {
    const { getValues, control, setValue } = useFormContext<OnboardingFormData>();
    const { fyiEntity } = getValues(`data.${groupIndex}.entities.${index}`);
    const selectField = useController({ name: `data.${groupIndex}.entities.${index}.isSelected` });

    const month = useWatch({ control, name: `data.${groupIndex}.entities.${index}.month` });
    const day = useWatch({ control, name: `data.${groupIndex}.entities.${index}.day` });
    const monthError = useWatch({ control, name: `data.${groupIndex}.entities.${index}.monthError` });
    const dayError = useWatch({ control, name: `data.${groupIndex}.entities.${index}.dayError` });

    function handleSelect(checked: boolean): void {
        selectField.field.onChange(checked);

        if (monthError) {
            setValue(`data.${index}.group.monthError`, undefined);
        }

        if (dayError) {
            setValue(`data.${index}.group.dayError`, undefined);
        }

        setValue(`data.${groupIndex}.group.isSelected`, true);

        // Toggle all select
        if (getValues(`data`).every((d) => d.group.isSelected)) {
            setValue(`globalSelected`, true);
        } else {
            setValue(`globalSelected`, false);
        }
    }

    function handleMonth(month: number): void {
        setValue(`data.${groupIndex}.entities.${index}.day`, undefined);
        setValue(`data.${groupIndex}.entities.${index}.month`, month);
        if (monthError) {
            setValue(`data.${groupIndex}.entities.${index}.monthError`, undefined);
        }
        if (dayError) {
            setValue(`data.${groupIndex}.entities.${index}.dayError`, undefined);
        }

        // Clear group row day/month error when all errors on entity rows are cleared
        if (getValues(`data.${groupIndex}.entities`).every((e) => !e.monthError && !e.dayError)) {
            setValue(`data.${groupIndex}.group.monthError`, undefined);
            setValue(`data.${groupIndex}.group.dayError`, undefined);
        }
    }

    function handleDay(day: number): void {
        setValue(`data.${groupIndex}.entities.${index}.day`, day);
        if (dayError) {
            setValue(`data.${groupIndex}.entities.${index}.dayError`, undefined);
        }

        // Clear group row day/month error when all errors on entity rows are cleared
        if (getValues(`data.${groupIndex}.entities`).every((e) => !e.monthError && !e.dayError)) {
            setValue(`data.${groupIndex}.group.monthError`, undefined);
            setValue(`data.${groupIndex}.group.dayError`, undefined);
        }
    }

    return (
        <li className="list-none">
            <div className="flex gap-1 py-3 items-center">
                <Checkbox
                    name={selectField.field.name}
                    id={selectField.field.name}
                    value={selectField.field.value}
                    checked={selectField.field.value}
                    onChange={(e): void => handleSelect(e.target.checked)}
                />
                <div className="ml-2 flex items-center gap-4">
                    <label className="text-gray-800 text-sm" htmlFor={selectField.field.name}>
                        {fyiEntity.name}
                    </label>
                    <BusinessStructureBadge type={fyiEntity.details.businessStructure} />
                </div>
                <div className="ml-auto flex items-center gap-2">
                    <div className="w-[150px]">
                        <Controller
                            name={`data.${groupIndex}.entities.${index}.month`}
                            render={(): JSX.Element => (
                                <MonthSelect
                                    onChange={(m): void => {
                                        handleMonth(m[0]);
                                    }}
                                    defaultValue={month}
                                    size="small"
                                    errorMessage={monthError}
                                />
                            )}
                        />
                    </div>
                    <div className="w-[150px]">
                        <Controller
                            name={`data.${groupIndex}.entities.${index}.day`}
                            render={(): JSX.Element => (
                                <DaySelect
                                    onChange={(d): void => {
                                        handleDay(d[0]);
                                    }}
                                    defaultValue={day}
                                    size="small"
                                    selectedMonth={month}
                                    errorMessage={dayError}
                                />
                            )}
                        />
                    </div>
                </div>
            </div>
        </li>
    );
}

export default FYIEntityRow;
